import type { BaseQueryApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getOfflineQueryData, setOfflineQueryData } from '../../db/odin-db-client';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { onlineStatusService } from '../../services/online-status.service';
import { baseQueryWithReAuthAndUrlEncoding } from './api-util';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type DefinitionExtraOptions = {};

/**
 * Base query wrapper that adds offline caching for GET requests.
 *
 * When REACT_APP_OFFLINE_DATABASE_ENABLED === 'true' and the browser is offline,
 * it attempts to serve cached data (unless the endpoint is blocklisted).
 *
 * While online, successful GET responses are persisted for future offline use.
 *
 * @param args Original RTK Query fetch args (string URL or FetchArgs).
 * @param api RTK Query API utilities (used to access the endpoint name).
 * @param extraOptions Additional options passed to the underlying base query.
 *
 * @returns Cached data when offline (if available) or the delegated base query result; otherwise a custom error.
 *
 * @remarks
 * Endpoints in OFFLINE_ENDPOINT_BLOCKLIST are never cached or served from offline storage.
 *
 * @see getOfflineData
 * @see setOfflineData
 */
export async function offlineAwareBaseQuery(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: DefinitionExtraOptions
) {
  if (isOfflineEnabled && isGetRequest(args)) {
    const offline = onlineStatusService.getStatus() === false;
    const endpointName = api.endpoint;

    // Offline GET: try to serve from database
    if (offline) {
      if (!offlineGetEndpointsBlocklist.has(endpointName)) {
        const keyArgs = typeof args === 'string' ? { url: args } : { ...(args.params || {}), url: args.url };
        const offlineData = await getOfflineQueryData(endpointName, keyArgs);
        if (offlineData) {
          return { data: offlineData.data };
        }
      }

      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'OFFLINE_NO_DATA',
        } as FetchBaseQueryError,
      };
    }

    // Online GET: delegate to existing baseQuery and store in db if successful
    const result = await baseQueryWithReAuthAndUrlEncoding(args, api, extraOptions);
    if (result.data && !offlineGetEndpointsBlocklist.has(endpointName)) {
      const keyArgs = typeof args === 'string' ? { url: args } : { ...(args.params || {}), url: args.url };
      setOfflineQueryData(endpointName, keyArgs, result.data);
    }

    return result;
  }

  return baseQueryWithReAuthAndUrlEncoding(args, api, extraOptions);
}

// Utility to check if the request is a GET
const isGetRequest = (args: string | FetchArgs) => {
  if (typeof args === 'string') return true;
  const method = (args.method || 'GET').toUpperCase();
  return method === 'GET';
};

// Endpoints that should NOT be cached for offline use
const offlineGetEndpointsBlocklist = new Set([
  // Files / binary / images
  'getFileUrl',
  'getFilesByRecordId',
  'getFilesByRecordIdAndQuestionId',
  'getPresignedUrl',
  'getFieldImageUrl',
  'getUserAvatar',
  'getCompanyLogo',

  // Attachments job
  'getImportAttachmentsJobDetails',

  // Export & template endpoints
  'exportRecord',
  'getRecordFieldTemplateExport',

  // Polling / background job status
  'getMutateRecordBackgroundJobDetails',
  'getImportJobDetails',
  'getAnalysisJobStatus',
  'getImportFormJob',
  'getGenerateRecordFieldValuesJobDetails',

  // Volatile counts
  // 'getAssignedRecordsCount',
  // 'getRecordCount',

  // Audit / notification logs
  'getAuditLogs',
  'getAuditLogDetails',
  'getGroupAuditLog',
  'getNotificationLog',
  'getRecordNotifications',

  // Misc
  'getFeatureTours',
]);
